// Browser-only helpers for downloads: loading ExcelJS on demand, saving a
// Blob or a server-prepared file, and turning images into PNG data URLs
// for spreadsheets.

// Code-split: the library (~1 MB) loads the first time someone exports.
export const loadExcelJS = () =>
  import('exceljs').then((mod) => (mod && mod.default) || mod);

// Clicks a temporary link. `filename` names the saved file where the
// browser honours it (blob: and same-origin links).
const clickLink = (href, filename) => {
  const a = document.createElement('a');
  a.href = href;
  if (filename) a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const downloadBlob = (filename, blob) => {
  const url = URL.createObjectURL(blob);
  clickLink(url, filename);
  // Revoking straight after click() can cancel the download in some
  // browsers; give it a moment.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// A file the server prepared (an export's signed link). The link answers
// with Content-Disposition: attachment, so following it saves the file
// and this page stays where it is.
export const downloadUrl = (url, filename) => clickLink(url, filename);

const SIZE = 160;

// The image drawn into a square PNG (contained, centred), or null when it
// can't be loaded or read back (an image host without CORS taints the
// canvas).
export const imageToPng = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = SIZE;
        canvas.height = SIZE;
        const ctx = canvas.getContext('2d');
        const scale = Math.min(SIZE / img.width, SIZE / img.height) || 1;
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, (SIZE - w) / 2, (SIZE - h) / 2, w, h);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

// The navy monogram tile as a PNG data URL (null without canvas support).
export const monogramPng = (text) => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0F0F23';
    if (ctx.roundRect) {
      ctx.beginPath();
      ctx.roundRect(0, 0, SIZE, SIZE, 36);
      ctx.fill();
    } else {
      ctx.fillRect(0, 0, SIZE, SIZE);
    }
    ctx.fillStyle = '#FFFFFF';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `800 ${
      text.length > 2 ? 44 : 60
    }px system-ui, -apple-system, "Segoe UI", sans-serif`;
    ctx.fillText(text, SIZE / 2, SIZE / 2 + 6);
    return canvas.toDataURL('image/png');
  } catch (e) {
    return null;
  }
};
